/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import React, { JSX, useMemo } from "react";
import "./style.scss";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell as MTableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow as MTableRow,
  TableRowProps,
  Typography
} from "@mui/material";
import classNames from "../../helpers/classNames";

export const TableCell = ({
  children,
  className,
  isHeaderCell = false,
  ...cellProps
}: TableCellProps & { children: React.ReactNode; isHeaderCell?: boolean }): JSX.Element => (
  <MTableCell
    {...cellProps}
    className={classNames(
      "nestedHeaderTableCell",
      isHeaderCell && "nestedHeaderTableHeaderCell",
      className
    )}
  >
    {children}
  </MTableCell>
);

export const TableRow = ({
  children,
  ...rowProps
}: TableRowProps & { children: React.ReactNode }): JSX.Element => (
  <MTableRow {...rowProps}>{children}</MTableRow>
);

export const EmptyView = ({
  content = "There are no items to display..."
}: {
  content?: React.ReactNode;
}): JSX.Element => (
  <Box className="emptyViewContainer">
    <div className="emptyListViewImage" />
    <Typography textAlign="center" marginTop="16px">
      {content}
    </Typography>
  </Box>
);

export const LoadingView = (): JSX.Element => (
  <Box className="loadingViewContainer">
    <CircularProgress />
  </Box>
);

export const getSerialNumber = ({
  pageSize,
  page,
  currentIndex
}: {
  pageSize: number;
  page: number;
  currentIndex: number;
}): number => page * pageSize + currentIndex + 1;

// Types
interface BaseColumn<T> {
  key: string;
  label: string;
  formatter?: (row: T, index: number) => React.ReactNode;
}

interface Column<T> extends BaseColumn<T> {
  children?: Column<T>[];
}

interface Props<T> {
  data: T[];
  columns: Column<T>[][];
  dataTestmation: string;
  pagination?: {
    rowsPerPage: number;
    page: number;
    onPageChange: (page: number) => void;
    total: number;
  };
  minWidth?: number;
  isLoading?: boolean;
  emptyViewContent?: React.ReactNode;
  dense?: boolean;
  onRowClick?: (row: T) => void;
}

// Helpers
const getMaxDepth = <T,>(cols: Column<T>[], depth = 1): number =>
  cols.reduce((max, col) => {
    if (col.children?.length) {
      return Math.max(max, getMaxDepth(col.children, depth + 1));
    }
    return Math.max(max, depth);
  }, depth);

const buildHeaderMatrix = <T,>(columns: Column<T>[], depth: number) => {
  const matrix: Array<
    Array<{
      label: string;
      key: string;
      colSpan: number;
      rowSpan: number;
    }>
  > = [];

  const fillMatrix = (cols: Column<T>[], level = 0): number => {
    matrix[level] ||= [];

    let colSpanSum = 0;

    cols.forEach((col) => {
      const children =
        Array.isArray(col.children) && col.children.length > 0 ? col.children : undefined;

      if (children) {
        const colSpan = fillMatrix(children, level + 1);
        const rowSpan = 1;

        matrix[level].push({
          label: col.label,
          key: col.key,
          colSpan,
          rowSpan
        });

        colSpanSum += colSpan;
      } else {
        const colSpan = 1;
        const rowSpan = depth - level;

        matrix[level].push({
          label: col.label,
          key: col.key,
          colSpan,
          rowSpan
        });

        colSpanSum += colSpan;
      }
    });

    return colSpanSum;
  };

  fillMatrix(columns, 0);
  return matrix;
};

const flattenColumns = <T,>(cols: Column<T>[]): BaseColumn<T>[] =>
  cols.flatMap((col) => (col.children?.length ? flattenColumns(col.children) : [col]));

// Main Component
const ListWithNestedHeader = <T,>({
  data,
  columns,
  dataTestmation,
  pagination,
  minWidth = 900,
  isLoading = false,
  emptyViewContent,
  dense = false,
  onRowClick
}: Props<T>): JSX.Element => {
  const flattenedColumns = useMemo(() => flattenColumns(columns[0]), [columns]);
  const maxDepth = useMemo(() => getMaxDepth(columns[0]), [columns]);
  const headerMatrix = useMemo(() => buildHeaderMatrix(columns[0], maxDepth), [columns, maxDepth]);

  return (
    <>
      <TableContainer
        data-testmation={dataTestmation}
        className="nestedHeaderList"
        sx={{ overflowX: "auto" }}
      >
        <Table size={dense ? "small" : "medium"} className="nestedHeaderTable" sx={{ minWidth }}>
          <TableHead>
            {headerMatrix.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell) => (
                  <TableCell
                    isHeaderCell
                    key={cell.key}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                  >
                    {cell.label}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                hover
                className={onRowClick ? "tableRowHover" : ""}
                onClick={() => onRowClick?.(row)}
              >
                {flattenedColumns.map((col) => (
                  <TableCell key={col.key}>{col.formatter?.(row, rowIndex) ?? null}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {isLoading && <LoadingView />}
        {!isLoading && data.length === 0 && <EmptyView content={emptyViewContent} />}
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          slotProps={{
            actions: {
              previousButton: { disabled: pagination.page === 0 || isLoading },
              nextButton: {
                disabled:
                  pagination.page >= Math.ceil(pagination.total / pagination.rowsPerPage) - 1 ||
                  isLoading
              }
            }
          }}
          rowsPerPageOptions={[]}
          count={pagination.total}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={(_, page) => pagination.onPageChange(page)}
        />
      )}
    </>
  );
};

export default ListWithNestedHeader;
